"use client";

import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useRef,
} from "react";
import { useEditor } from "../hooks/useEditor";

/**
 * ## Hook picker: which hook do I use?
 *
 * | Hook                        | Returns                          | Re-renders when                                 | Use for                                                               |
 * | --------------------------- | -------------------------------- | ----------------------------------------------- | --------------------------------------------------------------------- |
 * | `useEditorStable()`         | editor minus `localVideo`        | A non-`localVideo` field changes (rare)         | Toolbars, time-area, undo/redo, handlers that don't read `localVideo` |
 * | `useEditorLocalVideo()`     | `editor.localVideo` (live)       | Every `updateVideo` (~60 Hz during a trim)      | Timeline, player compositions, any per-tick consumer                  |
 * | `useEditorLocalVideoIdle()` | `editor.localVideo` (idle-snap)  | Every `updateVideo` (same as live)              | Secondary panels (transcript, captions, style, crop) that only need to reflect state at rest — returns the pre-drag snapshot while `isDragging` is true, so downstream memos skip per-tick work |
 * | `useEditorContext()`        | full `editor` (stable + live)    | Every `updateVideo` + any stable-side change    | Legacy / convenience / migration lag — avoid for new code             |
 *
 * Decision tree:
 *  1. Does the component read `localVideo.*` in its render / in a derivation consumed by the render?
 *     - No: `useEditorStable()`. Done.
 *     - Yes: go to 2.
 *  2. Does the derivation need to update at trim-drag-tick frequency (clip positions, playhead)?
 *     - Yes: `useEditorLocalVideo()`.
 *     - No: `useEditorLocalVideoIdle()`.
 *
 * `useEditorLocalVideoIdle` does NOT stop React from re-rendering the
 * caller — context subscriptions still fire. What it does: return the
 * SAME `localVideo` reference frame-to-frame during a drag, so every
 * downstream `useMemo` keyed on `localVideo` / `tracks` identity skips.
 * Don't defeat it by moving expensive work out of `useMemo` into the
 * render body.
 */
export type EditorValue = ReturnType<typeof useEditor>;

/**
 * Single source of truth for the field we split out of the editor
 * value. Typed against `EditorValue` so renaming the field on the
 * hook surface gives us a compile error at this constant AND at the
 * runtime comparison inside `useStableEditorValue`.
 */
const LOCAL_VIDEO_KEY = "localVideo" as const satisfies keyof EditorValue;

export type EditorStableValue = Omit<EditorValue, typeof LOCAL_VIDEO_KEY>;
export type EditorLocalVideo = EditorValue[typeof LOCAL_VIDEO_KEY];

/**
 * Legacy / backward-compat context. Carries the full `editor` object
 * (including `localVideo`). Consumers of this context re-render on
 * every `updateVideo` — which is what we want to stop for components
 * that don't actually read `localVideo`.
 */
export const EditorContext = createContext<EditorValue | undefined>(undefined);

/**
 * Stable context. Carries `editor` WITHOUT `localVideo`. Its value is
 * referentially stable across renders where only `localVideo`
 * changed — so consumers that only need the API surface (updaters,
 * selection helpers, player refs, timeline, etc.) don't re-render on
 * every trim-drag tick. Components that read `localVideo.*` should
 * keep using `useEditorContext()` (full merged view) or combine
 * `useEditorStable()` + `useEditorLocalVideo()`.
 */
export const EditorStableContext = createContext<EditorStableValue | undefined>(
  undefined,
);

/**
 * Sentinel used as the default for `EditorLocalVideoContext` so we
 * can distinguish "provider missing" from "localVideo is legitimately
 * undefined/null during initial load". Private to this module.
 */
const UNSET = Symbol("EditorLocalVideoContext/unset");

/**
 * Local-video context. Carries just `editor.localVideo`. Changes on
 * every `updateVideo`. Pair with `useEditorStable()` for fine-grained
 * subscriptions.
 */
export const EditorLocalVideoContext = createContext<
  EditorLocalVideo | typeof UNSET
>(UNSET);

/**
 * Returns the full editor object (stable API + current `localVideo`).
 * Re-renders on every `updateVideo`. Backward-compatible with every
 * existing consumer.
 */
export const useEditorContext = (): EditorValue => {
  const value = useContext(EditorContext);
  if (process.env.NODE_ENV !== "production" && value === undefined) {
    throw new Error(
      "useEditorContext() must be called inside <EditorProviders>.",
    );
  }
  return value as EditorValue;
};

/**
 * Returns the editor API without `localVideo`. Referentially stable
 * across `updateVideo`-only renders. Prefer this for toolbars,
 * button rows, and any component that calls editor methods but
 * doesn't read `localVideo.*` in its render.
 */
export const useEditorStable = (): EditorStableValue => {
  const value = useContext(EditorStableContext);
  if (process.env.NODE_ENV !== "production" && value === undefined) {
    throw new Error(
      "useEditorStable() must be called inside <EditorProviders>.",
    );
  }
  return value as EditorStableValue;
};

/**
 * Returns just `editor.localVideo`. Re-renders only when
 * `localVideo` actually changes (which happens on every
 * `updateVideo`; still useful when combined with `useEditorStable`
 * for everything else).
 */
export const useEditorLocalVideo = (): EditorLocalVideo => {
  const value = useContext(EditorLocalVideoContext);
  if (process.env.NODE_ENV !== "production" && value === UNSET) {
    throw new Error(
      "useEditorLocalVideo() must be called inside <EditorProviders>.",
    );
  }
  return value as EditorLocalVideo;
};

/**
 * Freeze `value` at its most recent pre-`active` reading while
 * `active` is true; return live `value` otherwise. The snapshot is
 * taken on the rising edge of `active` — so callers pass the CURRENT
 * value and get the pre-active snapshot back while `active === true`.
 *
 * Writes to refs during render, which is a documented React pattern
 * for "compute-during-render state" and is safe because the refs are
 * local to this hook and aren't observed elsewhere.
 *
 * Used by the editor split to let secondary panels skip downstream
 * work during a trim drag (see `useEditorLocalVideoIdle`), and by
 * `EditorPlayer` to freeze the main player's `tracks` prop while the
 * ghost overlay covers it.
 */
export function useFrozenWhile<T>(value: T, active: boolean): T {
  const frozenRef = useRef(value);
  const prevActiveRef = useRef(active);
  if (active && !prevActiveRef.current) {
    frozenRef.current = value;
  }
  prevActiveRef.current = active;
  return active ? frozenRef.current : value;
}

/**
 * Drop-in sibling of `useEditorLocalVideo()` that returns the
 * pre-drag snapshot while a user gesture is in flight (keyed on the
 * global `isDragging` flag maintained by the trim drag hook), and
 * the live `localVideo` otherwise.
 *
 * Important nuance: this hook does NOT stop the caller from
 * re-rendering when `localVideo` changes — React's context
 * subscription still fires. What it DOES do is return the same
 * `localVideo` reference frame-to-frame during a drag, so every
 * downstream memo / derivation keyed on `localVideo` or
 * `localVideo.tracks` identity skips. For secondary panels
 * (TranscriptEditor, CaptionEditor, StyleEditor, CropEditor, etc.)
 * that don't need per-tick updates, this pushes the expensive work
 * (trimClips, caption relative conversion, layout derivations) out
 * of the drag hot path and back onto drag-end, where the final state
 * settles once.
 *
 * The `isDragging` flag is explicit (not a proxy on another piece of
 * state), so future gestures (clip reorder, scrub, multi-select
 * drag) can raise it with the same semantics — every idle-subscriber
 * automatically benefits.
 */
export const useEditorLocalVideoIdle = (): EditorLocalVideo => {
  const localVideo = useEditorLocalVideo();
  const { isDragging } = useEditorStable();
  return useFrozenWhile(localVideo, isDragging);
};

/**
 * Compute a referentially-stable "stable editor" (editor minus
 * `localVideo`) that only changes identity when a non-`localVideo`
 * field actually changed. Shallow-walks the editor's own keys in a
 * single pass, allocates a new object ONLY on a miss — on the common
 * `updateVideo`-only path the previous reference is returned
 * untouched.
 *
 * Purpose-built (not generic) so the call site doesn't need casts
 * and the types line up exactly with `EditorStableValue`.
 */
function useStableEditorValue(editor: EditorValue): EditorStableValue {
  const lastRef = useRef<EditorStableValue | null>(null);

  const prev = lastRef.current;
  let changed = prev === null;
  if (prev !== null) {
    const prevRecord = prev as unknown as Record<string, unknown>;
    const nextRecord = editor as unknown as Record<string, unknown>;
    // `editor` is a literal object returned each render by `useEditor`;
    // its key set is stable across renders, so we only need to compare
    // values. We skip the excluded field inline without building a Set.
    for (const key of Object.keys(nextRecord)) {
      if (key === LOCAL_VIDEO_KEY) continue;
      if (prevRecord[key] !== nextRecord[key]) {
        changed = true;
        break;
      }
    }
  }

  if (changed) {
    // Build the stable slice by copying every own key except
    // `LOCAL_VIDEO_KEY`. Avoids the `const { localVideo: _lv, ...rest }`
    // destructure + unused-var dance.
    const rest: Record<string, unknown> = {};
    const editorRecord = editor as unknown as Record<string, unknown>;
    for (const key of Object.keys(editorRecord)) {
      if (key === LOCAL_VIDEO_KEY) continue;
      rest[key] = editorRecord[key];
    }
    lastRef.current = rest as EditorStableValue;
  }
  return lastRef.current!;
}

/**
 * Composite provider that wires the three editor contexts in one
 * place. Callers (Editor.tsx) shouldn't need to know how the split
 * works — they just pass the full `editor` value in. If the split
 * shape evolves (e.g. adding a fourth narrow context), it's
 * encapsulated here.
 */
export const EditorProviders: FC<{
  value: EditorValue;
  children: ReactNode;
}> = ({ value, children }) => {
  const stable = useStableEditorValue(value);
  return (
    <EditorStableContext.Provider value={stable}>
      <EditorLocalVideoContext.Provider value={value.localVideo}>
        <EditorContext.Provider value={value}>{children}</EditorContext.Provider>
      </EditorLocalVideoContext.Provider>
    </EditorStableContext.Provider>
  );
};

export const EditorRootContext = createContext<HTMLElement | null>(null);
